import { useNavigate } from 'react-router-dom';
import { Badge, Button, Icon } from '@/components/atoms';
import type { Return } from '@/schemas/returns';
import { formatCurrency, formatDateTime } from '@/utils/formatters';

interface ReturnDetailProps {
  returnData: Return;
  onApprove?: () => void;
  onReject?: () => void;
  onProcessRefund?: () => void;
  isLoading?: boolean;
}

export default function ReturnDetail({
  returnData,
  onApprove,
  onReject,
  onProcessRefund,
  isLoading,
}: ReturnDetailProps) {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'approved':
        return <Badge variant="info">Approved</Badge>;
      case 'refunded':
        return <Badge variant="success">Refunded</Badge>;
      case 'rejected':
        return <Badge variant="error">Rejected</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  const canApprove = returnData.status === 'pending';
  const canReject = returnData.status === 'pending';
  const canProcessRefund = returnData.status === 'approved';

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Return Details</h2>
            <p className="text-sm text-gray-500 mt-1">Return ID: {returnData.id}</p>
          </div>
          <div>{getStatusBadge(returnData.status)}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Return Date</p>
            <p className="text-base text-gray-900">{formatDateTime(returnData.return_date)}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Sale ID</p>
            <button
              onClick={() => navigate(`/sales/${returnData.sale_id}`)}
              className="text-base text-blue-600 hover:text-blue-800 underline"
            >
              {returnData.sale_id.substring(0, 8)}
            </button>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Refund</p>
            <p className="text-base font-bold text-gray-900">
              {formatCurrency(returnData.total_refund)}
            </p>
          </div>
        </div>
      </div>

      {/* Items Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Returned Items</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Refund Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {returnData.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.batch_id.substring(0, 8)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(item.refund_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={2} className="px-6 py-4 text-sm font-semibold text-gray-900">
                  Total
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  {formatCurrency(returnData.total_refund)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                <Icon name="Check" className="h-4 w-4 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Return Created</p>
              <p className="text-sm text-gray-500">{formatDateTime(returnData.created_at)}</p>
            </div>
          </div>

          {returnData.status !== 'pending' && (
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div
                  className={`h-8 w-8 rounded-full flex items-center justify-center ${
                    returnData.status === 'approved' || returnData.status === 'refunded'
                      ? 'bg-blue-100'
                      : 'bg-red-100'
                  }`}
                >
                  <Icon
                    name={
                      returnData.status === 'approved' || returnData.status === 'refunded'
                        ? 'Check'
                        : 'X'
                    }
                    className={`h-4 w-4 ${
                      returnData.status === 'approved' || returnData.status === 'refunded'
                        ? 'text-blue-600'
                        : 'text-red-600'
                    }`}
                  />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">
                  Return {returnData.status === 'rejected' ? 'Rejected' : 'Approved'}
                </p>
                <p className="text-sm text-gray-500">{formatDateTime(returnData.updated_at)}</p>
              </div>
            </div>
          )}

          {returnData.status === 'refunded' && (
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Icon name="Check" className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">Refund Processed</p>
                <p className="text-sm text-gray-500">{formatDateTime(returnData.updated_at)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {(canApprove || canReject || canProcessRefund) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
          <div className="flex space-x-4">
            {canApprove && onApprove && (
              <Button variant="primary" onClick={onApprove}>
                <Icon name="Check" className="mr-2 h-4 w-4" />
                Approve Return
              </Button>
            )}
            {canReject && onReject && (
              <Button variant="error" onClick={onReject}>
                <Icon name="X" className="mr-2 h-4 w-4" />
                Reject Return
              </Button>
            )}
            {canProcessRefund && onProcessRefund && (
              <Button variant="success" onClick={onProcessRefund}>
                <Icon name="DollarSign" className="mr-2 h-4 w-4" />
                Process Refund
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
